import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import AwaitingEvidenceWarning from "@cle/components/AwaitingEvidenceWarning";
import CleButton from "@cle/components/CleButton";
import CleModal, {
  CleModalRoot,
  CleModalTrigger,
} from "@cle/components/CleModal";
import type { AwaitingEvidenceItem } from "@cle/models/awaitingEvidence";
import ReportReceiptEmailStep, {
  type ReportReceiptEmailValues,
} from "./ReportReceiptEmailStep";
import ReportSubmitWarningStep from "./ReportSubmitWarningStep";
import {
  getNextReportSubmitDialogStep,
  getReportSubmitDialogSteps,
  type ReportSubmitDialogStep,
} from "./reportSubmitDialogSteps";

const STEP_TITLES: Record<ReportSubmitDialogStep, string> = {
  awaiting: "Du har dokumentation, der afventer behandling",
  warning: "Bekræft manglende lektioner",
  receipt: "Godkend indberetning",
};

type ReportSubmitDialogProps = {
  /** Dokumentation, der afventer behandling. Gentager advarslen fra flowets start lige
   * før indsendelsen, hvor indberetningen ikke længere kan ændres. */
  awaitingEvidence?: AwaitingEvidenceItem[];
  dialogDescription?: string;
  isPreparing?: boolean;
  isTriggerDisabled?: boolean;
  missingLessons: number;
  onPrepareForSigning: (receiptEmails: string[]) => Promise<void>;
  requiredLessons: number;
  selectedLessons: number;
  triggerId?: string;
  triggerLabel?: string;
};

const ReportSubmitDialog = ({
  awaitingEvidence = [],
  dialogDescription = "Godkend og indsend efteruddannelsesindberetning.",
  isPreparing = false,
  isTriggerDisabled = false,
  missingLessons,
  onPrepareForSigning,
  requiredLessons,
  selectedLessons,
  triggerId = "cle-submit-report",
  triggerLabel = "Godkend og indberet",
}: ReportSubmitDialogProps) => {
  const steps = getReportSubmitDialogSteps({
    awaitingEvidenceCount: awaitingEvidence.length,
    missingLessons,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<ReportSubmitDialogStep>(steps[0]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setSubmitError(null);

    if (open) {
      setStep(steps[0]);
    }
  };

  const handleNextStep = () => {
    setSubmitError(null);
    setStep(getNextReportSubmitDialogStep(steps, step));
  };

  const handleCancel = () => {
    setIsOpen(false);
    setSubmitError(null);
  };

  const handleReceiptSubmit = async (values: ReportReceiptEmailValues) => {
    const receiptEmails = [values.email, values.email2].filter(
      (email): email is string => Boolean(email?.trim()),
    );

    setSubmitError(null);

    try {
      await onPrepareForSigning(receiptEmails);
    } catch (error) {
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : "Indberetningen kunne ikke indsendes lige nu. Prøv igen.",
      );
    }
  };

  return (
    <CleModalRoot
      controller={{
        open: isOpen,
        setOpen: setIsOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <CleModalTrigger asChild>
        <CleButton
          id={triggerId}
          className="gap-2"
          disabled={isTriggerDisabled}
        >
          <CheckCircle2 size={16} aria-hidden strokeWidth={1.75} />
          {triggerLabel}
        </CleButton>
      </CleModalTrigger>

      <CleModal
        dialog
        size="sm"
        title={STEP_TITLES[step]}
        description={dialogDescription}
      >
        {step === "awaiting" ? (
          <AwaitingEvidenceWarning
            continueButtonId="cle-submit-awaiting-evidence-continue"
            continueLabel="Indberet alligevel"
            items={awaitingEvidence}
            onContinue={handleNextStep}
            onWait={handleCancel}
            waitButtonId="cle-submit-awaiting-evidence-wait"
            waitLabel="Vent med at indberette"
          />
        ) : step === "warning" ? (
          <ReportSubmitWarningStep
            missingLessons={missingLessons}
            onCancel={handleCancel}
            onConfirm={handleNextStep}
            requiredLessons={requiredLessons}
            selectedLessons={selectedLessons}
          />
        ) : (
          <ReportReceiptEmailStep
            errorMessage={submitError}
            isSubmitting={isPreparing}
            onCancel={handleCancel}
            onSubmit={handleReceiptSubmit}
          />
        )}
      </CleModal>
    </CleModalRoot>
  );
};

export default ReportSubmitDialog;
